import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { LoadingService } from '../../loading/loading.service';
import { UserService } from '../../user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { DialogModel } from 'src/app/components/dialog/models/DialogModel';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private requestStartTime: number = 0;

  constructor(
    private auth: UserService,
    private loadingService: LoadingService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.requestStartTime = Date.now();
    
    let authReq = request;
    const token = this.auth.getAcessToken();
    
    // Check if request is to our API
    const isApiRequest = request.url.includes(environment.apiUrl);
    const isAuthRequest = request.url.includes('auth/');
    
    if (token && isApiRequest) {
      authReq = this.addToken(request, token);
    } else if (isApiRequest && !isAuthRequest) {
      // Only show auth dialog for API requests that aren't auth endpoints
      this.openDialog('400px', {
        status: 'error',
        title: 'Authentication Required',
        content: 'You need to log in to access this resource.',
        okBtnText: 'Login',
      }).afterClosed().subscribe(() => {
        this.router.navigate(['/login']);
      });
      return throwError(() => new HttpErrorResponse({
        status: 401,
        error: { message: 'Authentication required' }
      }));
    }
    
    // Only show loading for API requests
    let checkInterval: any;
    if (isApiRequest) {
      checkInterval = setInterval(() => {
        if (Date.now() - this.requestStartTime > 1000) {
          this.loadingService.showLoading();
          clearInterval(checkInterval);
        }
      }, 100);
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Clear loading interval on error
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        
        if (error.status === 401 && isApiRequest && !isAuthRequest) {
          return this.handle401Error(authReq, next);
        } else if (error instanceof HttpErrorResponse && isApiRequest) {
          // Only show error dialogs for API requests
          this.showErrorDialog(error);
          
          // Scroll to top after showing error
          setTimeout(() => {
            requestAnimationFrame(() => {
              const content = document.querySelector('#app');
              if (content) content.scrollTop = 0;
            });
          }, 500);
        }
        
        return throwError(() => error);
      }),
      finalize(() => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
        if (isApiRequest) {
          this.loadingService.hideLoading();
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.auth.getRefreshToken();
      if (!refreshToken) {
        this.logoutAndNavigateToLogin();
        return throwError(() => new HttpErrorResponse({ status: 401, error: { message: 'No refresh token' } }));
      }

      return this.auth.refreshToken().pipe(
        switchMap((tokenResponse: any) => {
          this.isRefreshing = false;
          
          if (tokenResponse && tokenResponse.accessToken) {
            const newToken = tokenResponse.accessToken;
            this.auth.setAccessToken(newToken);
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addToken(request, newToken));
          } else {
            this.logoutAndNavigateToLogin();
            return throwError(() => new HttpErrorResponse({ status: 401, error: { message: 'Invalid token response' } }));
          }
        }),
        catchError((error: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.logoutAndNavigateToLogin();
          return throwError(() => error);
        })
      );
    } else {
      // Wait for refresh to complete
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  const headersConfig: { [name: string]: string } = {
    Authorization: `Bearer ${token}`,
  };

  // Only set Content-Type if the request doesn't have it already
  if (!request.headers.has('Content-Type') && !(request.body instanceof FormData)) {
    headersConfig['Content-Type'] = 'application/json';
  }

  return request.clone({
    setHeaders: headersConfig,
  });
}

  private logoutAndNavigateToLogin(): void {
    if (this.auth.isUserLogin()) {
      this.auth.logoutFromGoogleAccount();
    }
    this.router.navigate(['/login']);
  }

  private showErrorDialog(error: HttpErrorResponse): void {
    let title = 'Error';
    let content = 'An error occurred, please try again later.';

    if (error.error) {
      // Handle different error response formats
      if (error.error.error) {
        title = error.error.code_error ? `Error ${error.error.code_error}` : 'Error';
        content = error.error.error;
      } else if (error.error.message) {
        content = error.error.message;
      } else if (error.error.text) {
        content = error.error.text;
      } else if (error.error.error_message) {
        content = error.error.error_message;
      }
    }

    // Add status code to title if available
    if (error.status && error.status !== 200) {
      title = title.includes('Error') ? title : `Error ${error.status}`;
    }

    this.openDialog('400px', {
      status: 'error',
      title: title,
      content: content,
      okBtnText: 'Close',
    });
  }

  private openDialog(width: string = '400px', dialog: DialogModel) {
    return this.dialog.open(DialogComponent, {
      width: width,
      data: dialog
    });
  }
}